/**
 * PlacementDiff - Affiche les différences de placements
 * Placements ajoutés, supprimés, déplacés
 */
import { useState } from 'react';
import { OptimizationRun, Placement, Item3D } from '../../types/optimization';
import {
  CheckCircleIcon,
  MinusCircleIcon,
  ArrowPathIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

interface PlacementDiffProps {
  previousRun: OptimizationRun;
  newRun?: OptimizationRun;
  changesSummary?: {
    added_count?: number;
    removed_count?: number;
    moved_count?: number;
  };
}

interface MovedPlacement {
  item_id: string;
  from: Placement;
  to: Placement;
}

function placementItemId(p: Placement): string {
  return p.item_id || p.itemId || '';
}

export default function PlacementDiff({
  previousRun,
  newRun,
  changesSummary,
}: PlacementDiffProps) {
  const [expandedSections, setExpandedSections] = useState<
    Set<'added' | 'removed' | 'moved'>
  >(new Set(['added', 'removed', 'moved']));

  const toggleSection = (section: 'added' | 'removed' | 'moved') => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  // Calculer les différences
  const previousPlacements = previousRun.result?.placements || [];
  const newPlacements = newRun?.result?.placements || [];

  const previousIds = new Set(previousPlacements.map(placementItemId).filter(Boolean));
  const newIds = new Set(newPlacements.map(placementItemId).filter(Boolean));

  const added = newPlacements.filter((p) => !previousIds.has(placementItemId(p)));
  const removed = previousPlacements.filter((p) => !newIds.has(placementItemId(p)));

  // Helper pour trouver les dimensions
  const getItem = (itemId: string, run?: OptimizationRun): Item3D | undefined => {
    return run?.input.items.find(i => i.id === itemId);
  };

  // Déterminer les placements déplacés
  const moved: MovedPlacement[] = [];
  previousPlacements.forEach((oldPlacement) => {
    const itemId = placementItemId(oldPlacement);
    if (!itemId) return;
    const newPlacement = newPlacements.find((p) => placementItemId(p) === itemId);
    if (newPlacement) {
      const oldPos = [
        oldPlacement.position?.x || 0,
        oldPlacement.position?.y || 0,
        oldPlacement.position?.z || 0,
      ];
      const newPos = [
        newPlacement.position?.x || 0,
        newPlacement.position?.y || 0,
        newPlacement.position?.z || 0,
      ];
      if (JSON.stringify(oldPos) !== JSON.stringify(newPos)) {
        moved.push({
          item_id: itemId,
          from: oldPlacement,
          to: newPlacement,
        });
      }
    }
  });

  const totalChanges = added.length + removed.length + moved.length;
  const addedCount = changesSummary?.added_count || added.length;
  const removedCount = changesSummary?.removed_count || removed.length;
  const movedCount = changesSummary?.moved_count || moved.length;

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="text-xs text-gray-600 mb-1">TOTAL</div>
          <div className="text-2xl font-bold text-gray-900">{totalChanges}</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="text-xs text-gray-600 mb-1">AJOUTÉS</div>
          <div className="text-2xl font-bold text-green-700">{addedCount}</div>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <div className="text-xs text-gray-600 mb-1">SUPPRIMÉS</div>
          <div className="text-2xl font-bold text-red-700">{removedCount}</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="text-xs text-gray-600 mb-1">DÉPLACÉS</div>
          <div className="text-2xl font-bold text-blue-700">{movedCount}</div>
        </div>
      </div>

      {/* No Changes */}
      {totalChanges === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <CheckCircleIcon className="w-12 h-12 text-green-600 mx-auto mb-2" />
          <p className="text-gray-900 font-semibold">Aucun changement</p>
          <p className="text-sm text-gray-600 mt-1">
            L'optimisation a conservé la même configuration
          </p>
        </div>
      )}

      {/* Added Placements */}
      {addedCount > 0 && (
        <div className="border border-green-200 bg-green-50 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('added')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-green-100 transition"
          >
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-green-800">
                Articles Ajoutés ({addedCount})
              </span>
            </div>
            <ChevronDownIcon
              className={`w-4 h-4 text-green-600 transition-transform ${
                expandedSections.has('added') ? 'rotate-180' : ''
              }`}
            />
          </button>

          {expandedSections.has('added') && (
            <div className="px-4 py-3 bg-white space-y-2 border-t border-green-200">
              {added.map((placement, idx) => {
                const item = getItem(placementItemId(placement), newRun);
                return (
                  <div
                    key={idx}
                    className="p-3 bg-green-50 border border-green-200 rounded text-sm"
                  >
                    <div className="font-medium text-green-800">
                      {placement.item_id}
                    </div>
                    <div className="text-xs text-gray-600 mt-1 space-y-1">
                      <div>
                        Position: ({placement.position?.x || 0},
                        {placement.position?.y || 0},
                        {placement.position?.z || 0})
                      </div>
                      {item && (
                        <div>
                          Dimensions: {item.length}×{item.height}×{item.width}mm
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Removed Placements */}
      {removedCount > 0 && (
        <div className="border border-red-200 bg-red-50 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('removed')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-red-100 transition"
          >
            <div className="flex items-center gap-3">
              <MinusCircleIcon className="w-5 h-5 text-red-600" />
              <span className="font-semibold text-red-800">
                Articles Supprimés ({removedCount})
              </span>
            </div>
            <ChevronDownIcon
              className={`w-4 h-4 text-red-600 transition-transform ${
                expandedSections.has('removed') ? 'rotate-180' : ''
              }`}
            />
          </button>

          {expandedSections.has('removed') && (
            <div className="px-4 py-3 bg-white space-y-2 border-t border-red-200">
              {removed.map((placement, idx) => {
                const item = getItem(placementItemId(placement), previousRun);
                return (
                  <div
                    key={idx}
                    className="p-3 bg-red-50 border border-red-200 rounded text-sm"
                  >
                    <div className="font-medium text-red-800">
                      {placement.item_id}
                    </div>
                    <div className="text-xs text-gray-600 mt-1 space-y-1">
                      <div>
                        Position: ({placement.position?.x || 0},
                        {placement.position?.y || 0},
                        {placement.position?.z || 0})
                      </div>
                      {item && (
                        <div>
                          Dimensions: {item.length}×{item.height}×{item.width}mm
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Moved Placements */}
      {movedCount > 0 && (
        <div className="border border-blue-200 bg-blue-50 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('moved')}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-blue-100 transition"
          >
            <div className="flex items-center gap-3">
              <ArrowPathIcon className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-800">
                Articles Déplacés ({movedCount})
              </span>
            </div>
            <ChevronDownIcon
              className={`w-4 h-4 text-blue-600 transition-transform ${
                expandedSections.has('moved') ? 'rotate-180' : ''
              }`}
            />
          </button>

          {expandedSections.has('moved') && (
            <div className="px-4 py-3 bg-white space-y-3 border-t border-blue-200">
              {moved.map((m, idx) => (
                <div key={idx} className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <div className="font-medium text-blue-800 mb-2">{m.item_id}</div>
                  <div className="space-y-2 text-xs text-gray-600">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-red-600 font-semibold mb-1">Avant</div>
                        <div>
                          ({m.from.position?.x || 0},
                          {m.from.position?.y || 0},
                          {m.from.position?.z || 0})
                        </div>
                      </div>
                      <div>
                        <div className="text-green-600 font-semibold mb-1">Après</div>
                        <div>
                          ({m.to.position?.x || 0},
                          {m.to.position?.y || 0},
                          {m.to.position?.z || 0})
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>ℹ️ Note:</strong> Cette vue montre les différences détectées lors de la
          replanification. Les articles peuvent être ajoutés, supprimés ou avoir changé de
          position dans le chargement.
        </p>
      </div>
    </div>
  );
}
